/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { setGlobalValue, getGlobalValue, } from "@keeex/js-utils/lib/global.js";
import { makeProfilePredicate } from "@keeex/js-utils/lib/types/record.js";
import { isDigestProvider } from "./digesttypes.js";
import { isRandomProvider } from "./randomtypes.js";
import { isCipherProvider } from "./ciphertypes.js";
import { isKeysProvider } from "./keystypes.js";
import { isHmacProvider } from "./hmactypes.js";
/**
 * Current version of the API.
 * Should usually match the package major version.
 */
const MAJOR_VERSION_EXPECTED = 3;
const isCryptoProvider = makeProfilePredicate({
    majorVersion: "number",
    digestProvider: isDigestProvider,
    randomProvider: isRandomProvider,
    cipherProvider: isCipherProvider,
    keysProvider: isKeysProvider,
    hmacProvider: isHmacProvider,
});
const isProviderOptions = makeProfilePredicate({ noImmediateFallback: "boolean" });
const defaultProviderOptions = { noImmediateFallback: false };
/**
 * Currently active provider
 */
let activeProvider;
export const globalProviderKey = `kx_crypto${MAJOR_VERSION_EXPECTED}_provider`;
export const globalProviderOptionsKey = `kx_crypto${MAJOR_VERSION_EXPECTED}_provider_options`;
const loadGlobalOptions = () => {
    const globalValue = getGlobalValue(globalProviderOptionsKey);
    if (!isProviderOptions(globalValue)) {
        setGlobalValue(globalProviderOptionsKey, defaultProviderOptions);
    }
};
loadGlobalOptions();
export const setProviderOptions = (options) => {
    const globalValue = getGlobalValue(globalProviderOptionsKey);
    if (!isProviderOptions(globalValue))
        throw new Error("Unexpected state, global options invalid");
    Object.assign(globalValue, options);
    setGlobalValue(globalProviderOptionsKey, globalValue);
};
const validateProvider = (cryptoProvider) => {
    if (!cryptoProvider) {
        throw new Error("No cryptographic provider provided");
    }
    if (!isCryptoProvider(cryptoProvider)) {
        throw new Error("Provided cryptographic provider does not match expected interface");
    }
    if (cryptoProvider.majorVersion !== MAJOR_VERSION_EXPECTED) {
        // eslint-disable-next-line max-len
        throw new Error(`Cryptographic provider version mismatch (expected ${MAJOR_VERSION_EXPECTED}, got ${cryptoProvider.majorVersion})`);
    }
    return cryptoProvider;
};
/** Function used by providers to register themselves on loading. */
export const registerProvider = (cryptoProvider) => {
    const globalProviderSet = getGlobalValue(globalProviderKey);
    try {
        validateProvider(globalProviderSet);
        // eslint-disable-next-line no-console
        console.warn("Multiple cryptographic provider tried to register. This indicate dependencies issues.");
        return;
    }
    catch {
        // No provider registered
        validateProvider(cryptoProvider);
        setGlobalValue(globalProviderKey, cryptoProvider);
        activeProvider = cryptoProvider;
    }
};
/**
 * Change the provider to use
 *
 * @deprecated Providers are automatically registered on import, this function is not needed anymore
 * Setting provider options is done using `setProviderOptions()`.
 */
export const setProvider = (cryptoProvider, options) => {
    if (cryptoProvider) {
        registerProvider(cryptoProvider);
    }
    if (options) {
        setProviderOptions(options);
    }
};
/**
 * Return the current provider.
 *
 * Used internally by other cryptographic functions; you should not need to
 * call this when using the library.
 */
export const getProvider = () => {
    if (!activeProvider) {
        const globalProviderSet = validateProvider(getGlobalValue(globalProviderKey));
        activeProvider = globalProviderSet;
    }
    return activeProvider;
};
export const getProviderOptions = () => {
    const globalValue = getGlobalValue(globalProviderOptionsKey);
    if (!isProviderOptions(globalValue))
        throw new Error("Unexpected state, global options invalid");
    return { ...globalValue };
};
